import React, {useCallback, useEffect, useLayoutEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button} from '@mui/material';
import {useQueryParams} from '../../hooks/useQueryParams';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {UPDATES} from '../../shared/UPDATES';
import {useAutoForm} from '../AutoForm/useAutoForm';
import QuestionWrapper, {WizardField} from './QuestionWrapper';
import {handoverStepRoutes} from '../../pages/HandoverSummaryPage/HandoverStepTypes';

export type wizardFieldsType = {
  step: number;
};

export function Wizard<T extends wizardFieldsType>({
  formFields,
  goTo,
  SummaryComponent,
}: {
  formFields: WizardField<T>[];
  goTo: 'precursor' | 'meeting';
  SummaryComponent: React.FC<{
    formFields: WizardField<T>[];
    previousStep: () => void;
  }>;
}) {
  const {values, setFieldValue} = useAutoForm<T>();
  const params = useQueryParams();
  const activeStep = values.step;
  const navigate = useNavigate();
  useEffect(() => {
    if (params.get('skip') === 'true') {
      setFieldValue(UPDATES._3200, 'step', formFields.length);
    }
  }, [params]);
  /**
   * return to the last question with a formik value
   * set formik value to undefined as to reset the answer
   */
  const previousStep = useCallback(() => {
    let i = activeStep - 1;
    while (i > 0 && !formFields[i].enabled(values)) {
      i -= 1;
    }
    if (i > -1) {
      setFieldValue(UPDATES._3201, 'step', i);
    } else {
      navigate({
        pathname:
          goTo === 'precursor' ? `../../precursor` : `../${handoverStepRoutes.meeting}`,
      });
    }
  }, [formFields, activeStep, setFieldValue]);

  useLayoutEffect(() => {
    if (formFields[activeStep]) {
      if (!formFields[activeStep].enabled(values)) {
        setFieldValue(UPDATES._3202, 'step', activeStep + 1);
      }
    }
  }, [activeStep]);

  if (activeStep < formFields.length) {
    return (
      <>
        <QuestionWrapper
          key={formFields[activeStep].id} // required for this to refresh the form
          field={formFields[activeStep]}
          activeStep={activeStep}
          previousStep={previousStep}
          progress={(100 * activeStep) / formFields.length}
        />
        <ShowIfDebugWrapper>
          <Button onClick={() => setFieldValue(UPDATES._3203, 'step', formFields.length)}>
            Go to summary
          </Button>
        </ShowIfDebugWrapper>
      </>
    );
  }
  return (
    <>
      <SummaryComponent formFields={formFields} previousStep={previousStep} />
    </>
  );
}
